import { useRef, useEffect, useState, FC } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader, GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import groundFloorThumb from '../assets/ground_floor_thumb.png';
import firstFloorThumb from '../assets/first_floor_thumb.png';
import ceilingThumb from '../assets/ceiling_thumb.png';

import { PMREMGenerator } from 'three';

interface ModelViewerProps {
  initialModelPath?: string;
}

const ModelViewer: FC<ModelViewerProps> = ({ initialModelPath = '/assets/first_floor.glb' }) => {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const directionalLightRef = useRef<THREE.DirectionalLight | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());
  const [isLoading, setIsLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [_hoveredMeshName, setHoveredMeshName] = useState<string | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const mouseMoveListenerAttachedRef = useRef<boolean>(false);
  const lastHighlightChangeRef = useRef<number>(0);
  const autoRotateRef = useRef<boolean>(true);
  const userInteractedRef = useRef<boolean>(false);
  const modelCenterRef = useRef<THREE.Vector3 | null>(null);
  const initialCameraPositionRef = useRef<THREE.Vector3 | null>(null);
  const initialTargetRef = useRef<THREE.Vector3 | null>(null);
  //const frameCountRef = useRef<number>(0);
  const warmLightRef = useRef<THREE.PointLight | null>(null);
  const modelBoundsRef = useRef<THREE.Box3 | null>(null);
  const [currentModelPath, setCurrentModelPath] = useState<string>(initialModelPath);
  const [activeModelButton, setActiveModelButton] = useState<string>("firstFloor");
  const modelSizeRef = useRef<number>(1);
  const dracoLoaderRef = useRef<DRACOLoader | null>(null);
  const dracoLoaderInitializedRef = useRef<boolean>(false);
  const groundPlaneRef = useRef<THREE.Mesh | null>(null);
  const clickStartTimeRef = useRef<number>(0);
  const clickedPositionRef = useRef<THREE.Vector2>(new THREE.Vector2());
  const [_currentVariant, setCurrentVariant] = useState<'roof' | 'first' | 'ground'>('first');
  const clickTimeoutRef = useRef<number | null>(null);
  const enableHoverCheckingRef = useRef<boolean>(false);
  const isMobileRef = useRef<boolean>(false);
  const targetMeshesRef = useRef<Map<string, THREE.Mesh>>(new Map());
  const originalMaterialsRef = useRef<Map<string, THREE.Material | THREE.Material[]>>(new Map());
  const highlightMaterialRef = useRef<THREE.Material | null>(null);
  const currentlyHighlightedUnitRef = useRef<string | null>(null);
  const unitMeshesRef = useRef<Map<string, THREE.Mesh[]>>(new Map());
  const lowMemoryModeRef = useRef<boolean>(false);
  const modelGeometryOptimizedRef = useRef<boolean>(false);
  const modelChangeTimeoutRef = useRef<number | null>(null);
  const lastModelChangeTimeRef = useRef<number>(0);
  const devicePerformanceRef = useRef<'high'|'medium'|'low'>('high');
  
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [windowHeight, setWindowHeight] = useState(typeof window !== 'undefined' ? window.innerHeight : 800);
  const [showMobileHelp, setShowMobileHelp] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(() => {
    if (typeof navigator === 'undefined') return false;
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  });
  

  const detectDeviceCapabilities = () => {
    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    isMobileRef.current = isMobile;
  
    let performance: "high" | "medium" | "low" = "high";
  
    // Check for low-end mobile devices
    if (isMobile) {
      const isOlderIOS =
        /iPhone|iPad|iPod/.test(ua) && !(/iPhone|iPad|iPod.*OS 1[3-9]/.test(ua)); // iOS 12 or lower
  
      const isLowAndroid =
        /Android/.test(ua) &&
        (/Android [1-6]\./.test(ua) || // Android 6 or lower
          /Android 7\.[0-1]/.test(ua)); // Android 7.0-7.1
  
      // Safely read deviceMemory (not present in all TS lib.dom versions)
      const deviceMemoryValue = (navigator as any).deviceMemory;
      const isLowRAM = typeof deviceMemoryValue === "number" ? deviceMemoryValue < 4 : false;
  
      // Safely read hardwareConcurrency
      const hwConcurrency = (navigator as any).hardwareConcurrency;
      const lowCPU = typeof hwConcurrency === "number" ? hwConcurrency < 4 : false;
  
      if (isOlderIOS || isLowAndroid || isLowRAM || lowCPU) {
        performance = "low";
      } else {
        performance = "medium";
      }
    }
  
    const hasWebGL2 = !!(window as any).WebGL2RenderingContext;
    if (!hasWebGL2) {
      performance = "low";
    }
  
    devicePerformanceRef.current = performance;
    lowMemoryModeRef.current = performance === "low";
  
    console.log(`Device detected as: ${isMobile ? "Mobile" : "Desktop"}, Performance: ${performance}`);
  
    return { isMobile, performance };
  };
  
  
const getUnitNumberFromName = (name: string): string | null => {
  // Match pattern like "16_3", "7002_1", etc. and extract the unit number part
  const match = name.match(/^(\d+)_\d+$/);
  if (match && match[1]) {
    // For 3+ digit numbers, check if we need to take just the first 1-2 digits
    const fullNumber = match[1];
    if (fullNumber.length >= 3) {
      // For 3-4 digits, take the first digit
      // For 5+ digits, take the first two digits
      return fullNumber.length >= 5 ? fullNumber.substring(0, 2) : fullNumber.substring(0, 1);
    }
    return fullNumber; // Return as-is for 1-2 digit numbers
  }
  return null;
};
  
  const getTargetNumber = (name: string): number | null => {
    if (name.includes('Cars') || name.includes('human') || name.includes('Site') || 
        /^(?!.*\d).*$/.test(name)) { // Regex to catch names with no digits
      return null;
    }
    
    const endMatch = name.match(/_(\d+)$/);
    if (endMatch && endMatch[1]) {
      const num = parseInt(endMatch[1], 10);
      if (num >= 1 && num <= 69) return num;
    }
    
    const complexMatch = name.match(/Mesh\d+_(\d+)/);
    if (complexMatch && complexMatch[1]) {
      const num = parseInt(complexMatch[1], 10);
      if (num >= 1 && num <= 69) return num;
    }
    
    const anyNumberMatch = name.match(/\b([1-9]|[1-5][0-9]|6[0-9])\b/);
    if (anyNumberMatch && anyNumberMatch[1]) {
      const num = parseInt(anyNumberMatch[1], 10);
      if (num >= 1 && num <= 69) return num;
    }
    
    return null;
  };
  
  const initDracoLoader = () => { 
    if (!dracoLoaderRef.current) {
      console.log("Creating new Draco loader");
      const loader = new DRACOLoader();
      // Use the CDN path with specific version to ensure consistency
      loader.setDecoderPath('https://unpkg.com/three@0.159.0/examples/jsm/libs/draco/');
      
      // Use JS decoder for mobile devices which is more compatible
      loader.setDecoderConfig({ type: 'js' });
      dracoLoaderRef.current = loader;
      dracoLoaderInitializedRef.current = true;
    }
    return dracoLoaderRef.current;
  };
  
  const isTargetMesh = (name: string): boolean => {
    if (name === 'Cars' || name === 'human' || name === 'Site') {
      return false;
    }
    
    const targetNum = getTargetNumber(name);
    return targetNum !== null && targetNum >= 1 && targetNum <= 69;
  };
  
  const highlightUnitMeshes = (unitNumber: string | null) => {
    if (!unitNumber || unitNumber === currentlyHighlightedUnitRef.current) {
      return;
    }
    
    // Skip highlighting in very low memory mode on mobile
    if (lowMemoryModeRef.current && isMobileRef.current) {
      setHoveredMeshName(unitNumber);
      currentlyHighlightedUnitRef.current = unitNumber;
      return;
    }
    
    // Reset any previously highlighted unit first
    if (currentlyHighlightedUnitRef.current) {
      resetHighlighting();
    }
    
    // Highlight all meshes in the new unit
    const meshesToHighlight = unitMeshesRef.current.get(unitNumber) || [];
    if (highlightMaterialRef.current && meshesToHighlight.length > 0) {
      meshesToHighlight.forEach(mesh => {
        // Store original material if not already stored
        if (!originalMaterialsRef.current.has(mesh.uuid)) {
          originalMaterialsRef.current.set(mesh.uuid, mesh.material);
        }
        
        // Instead of cloning, create a simple highlight mesh for better performance
        const highlightMesh = mesh.clone();
        highlightMesh.material = highlightMaterialRef.current as THREE.Material;
        highlightMesh.renderOrder = 1;
        highlightMesh.userData.isHighlight = true;
        highlightMesh.userData.originalMeshId = mesh.uuid;
        
        // Add the highlight mesh to the scene
        if (mesh.parent) {
          mesh.parent.add(highlightMesh);
          // Store reference for later removal
          mesh.userData.highlightMesh = highlightMesh;
        }
      });
      
      // Set the unit name for the UI
      setHoveredMeshName(unitNumber);
    }
    
    currentlyHighlightedUnitRef.current = unitNumber;
  };
  
  const resetHighlighting = () => {
    if (!currentlyHighlightedUnitRef.current) return;
    
    const meshesToReset = unitMeshesRef.current.get(currentlyHighlightedUnitRef.current) || [];
    meshesToReset.forEach(mesh => {
      // Remove highlight mesh if it exists
      if (mesh.userData.highlightMesh) {
        if (mesh.parent) {
          mesh.parent.remove(mesh.userData.highlightMesh);
        }
        // Dispose of the highlight mesh geometry and material references
        if (mesh.userData.highlightMesh.geometry) {
          mesh.userData.highlightMesh.geometry = null;
        }
        mesh.userData.highlightMesh = null;
      }
    });
    
    currentlyHighlightedUnitRef.current = null;
    setHoveredMeshName(null);
  };
  
  const checkIntersection = () => {
    if (!cameraRef.current || targetMeshesRef.current.size === 0) return;
  
    const currentTime = Date.now();
    
    // Increase debounce time to reduce flickering - more aggressive throttling
    const baseDebounceTime = 200; // Increased from 50ms to 200ms
    if (currentTime - lastHighlightChangeRef.current < baseDebounceTime) {
      return; // Skip this check if too soon after last change
    }
  
    // Optionally adjust threshold based on camera distance
    const distanceToModel = cameraRef.current.position.distanceTo(modelCenterRef.current || new THREE.Vector3());
    raycasterRef.current.params.Mesh = {
      threshold: distanceToModel > 15 ? 0.08 : 0.02
    };
  
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
  
    // Use ALL target meshes consistently - no random subsampling to avoid flicker
    const targetMeshes = Array.from(targetMeshesRef.current.values());
  
    const intersects = raycasterRef.current.intersectObjects(targetMeshes, false);
  
    if (intersects.length === 0) {
      if (currentlyHighlightedUnitRef.current) {
        resetHighlighting();
        lastHighlightChangeRef.current = currentTime;
      }
      return;
    }
  
    const firstIntersect = intersects[0];
    if (!firstIntersect || !firstIntersect.object) return;
  
    const mesh = firstIntersect.object as THREE.Mesh;
    const unitNumber = getUnitNumberFromName(mesh.name);
  
    if (!unitNumber) {
      if (currentlyHighlightedUnitRef.current) {
        resetHighlighting();
        lastHighlightChangeRef.current = currentTime;
      }
      return;
    }
  
    // Only change highlight if it's a different unit
    if (unitNumber === currentlyHighlightedUnitRef.current) return;
  
    highlightUnitMeshes(unitNumber);
    lastHighlightChangeRef.current = currentTime;
  };
  
  // const updateHoverSensitivity = () => {
  //   // Always enable hover checking regardless of camera angle
  //   enableHoverCheckingRef.current = true;
  
  //   // Always check intersection if hover is enabled
  //   checkIntersection();
  // };
  
  
  const handleMeshClick = () => {
    if (!cameraRef.current || targetMeshesRef.current.size === 0) return;
    
    // Calculate if this was a quick click (not a drag)
    const clickEndTime = Date.now();
    const clickDuration = clickEndTime - clickStartTimeRef.current;
    
    if (clickDuration > 200) return;
    
    const currentMousePosition = mouseRef.current.clone();
    const distance = clickedPositionRef.current.distanceTo(currentMousePosition);
    if (distance > 0.05) return; // Mouse moved too much during click
    
    // Prevent multiple rapid clicks (debounce)
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    clickTimeoutRef.current = window.setTimeout(() => {
      if (cameraRef.current) {
        raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
      }
      
      // Optimize for mobile: reduce the number of meshes to check
      let targetMeshes: THREE.Mesh[];
      if (isMobileRef.current && lowMemoryModeRef.current && targetMeshesRef.current.size > 20) {
        // On mobile, check only a subset of meshes
        const allMeshes = Array.from(targetMeshesRef.current.values());
        targetMeshes = allMeshes.slice(0, Math.min(allMeshes.length, 30)); // Limit to 30 meshes
      } else {
        targetMeshes = Array.from(targetMeshesRef.current.values());
      }
      
      const intersects = raycasterRef.current.intersectObjects(targetMeshes, false);
      
      if (intersects.length === 0) return;
      
      const firstIntersect = intersects[0];
      if (!firstIntersect || !firstIntersect.object) return;
      
      const mesh = firstIntersect.object as THREE.Mesh;
      const unitNumber = getUnitNumberFromName(mesh.name);
      
      if (unitNumber) {
        // Navigate to the individual unit page with the current variant
        navigate(`/individual/${_currentVariant}/${unitNumber}`);
      }
      
      clickTimeoutRef.current = null;
    }, 300); // Debounce time in ms
  };

  const setupMouseEvents = () => {
    const container = containerRef.current;
    if (!container || mouseMoveListenerAttachedRef.current) return;
  
    const onMouseMove = (event: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
  
      enableHoverCheckingRef.current = true;
  
      // Throttle intersection checks on mouse move - the checkIntersection function now handles debouncing
      // Don't call on every mouse move to reduce flickering
    };
  
    
    
    const onTouchMove = (event: TouchEvent) => {
      if (!container) return;
      if (event.touches && event.touches.length > 0) {
        const touch = event.touches[0] ?? event.touches.item(0);
        if (!touch) return;
        const rect = container.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
  
        mouseRef.current.x = x;
        mouseRef.current.y = y;
  
        enableHoverCheckingRef.current = true;
        userInteractedRef.current = true;
        autoRotateRef.current = false;
  
        // Throttle touch move checks - the checkIntersection function now handles debouncing
      }
    };
      
    const onMouseDown = (event: MouseEvent) => {
      clickStartTimeRef.current = Date.now();
      const rect = container.getBoundingClientRect();
      clickedPositionRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      clickedPositionRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      userInteractedRef.current = true;
      autoRotateRef.current = false;
      
      // Ensure hover checking is enabled once user interacts
      enableHoverCheckingRef.current = true;
    };
    
    const onTouchStart = (event: TouchEvent) => {
      // guard container if it may be null
      if (!container) return;
    
      // ensure there's at least one touch and that touches[0] exists
      if (event.touches && event.touches.length > 0) {
        const touch = event.touches[0] ?? event.touches.item(0);
        if (!touch) return; // extra safety for some TS/DOM combos
    
        clickStartTimeRef.current = Date.now();
    
        const rect = container.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
    
        clickedPositionRef.current.x = x;
        clickedPositionRef.current.y = y;
    
        userInteractedRef.current = true;
        autoRotateRef.current = false;
      }
    };
    
    
    const onMouseUp = () => {
      handleMeshClick();
    };
    
    const onTouchEnd = () => {
      handleMeshClick();
    };
    
    const onUserInteraction = () => {
      userInteractedRef.current = true;
      autoRotateRef.current = false;
      
      // Ensure hover checking is enabled once user interacts
      enableHoverCheckingRef.current = true;
    };
    
    container.addEventListener('mousemove', onMouseMove);
    container.addEventListener('mousedown', onMouseDown);
    container.addEventListener('mouseup', onMouseUp);
    container.addEventListener('wheel', onUserInteraction);
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    mouseMoveListenerAttachedRef.current = true;
    
    return () => {
      container.removeEventListener('mousemove', onMouseMove);
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('mouseup', onMouseUp);
      container.removeEventListener('wheel', onUserInteraction);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      mouseMoveListenerAttachedRef.current = false;
    };
  };
  
  const resetCameraView = () => {
    if (!controlsRef.current || !cameraRef.current) return;
    
    if (initialCameraPositionRef.current && initialTargetRef.current) {
      const controls = controlsRef.current;
      const camera = cameraRef.current;
      
      const startPosition = camera.position.clone();
      const startTarget = controls.target.clone();
      
      const endPosition = initialCameraPositionRef.current.clone();
      const endTarget = initialTargetRef.current.clone();
      
      const duration = isMobileRef.current ? 800 : 1000; // Slightly faster on mobile
      const startTime = Date.now();
      
      const wasAutoRotating = autoRotateRef.current;
      autoRotateRef.current = false;
      
      const animateReset = () => {
        const now = Date.now();
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
        const easedProgress = easeOut(progress);
        
        camera.position.lerpVectors(startPosition, endPosition, easedProgress);
        controls.target.lerpVectors(startTarget, endTarget, easedProgress);
        controls.update();
        
        if (progress < 1) {
          requestAnimationFrame(animateReset);
        } else {
          autoRotateRef.current = wasAutoRotating;
        }
      };
      
      animateReset();
      
      userInteractedRef.current = true;
    } else if (modelCenterRef.current) {
      controlsRef.current.target.copy(modelCenterRef.current);
      controlsRef.current.update();
    }
  };

  const updateControlTargetBasedOnView = () => {
    if (!cameraRef.current || !controlsRef.current || !sceneRef.current) return;
  
    // Skip this optimization on low-end mobile devices
    if (isMobileRef.current && lowMemoryModeRef.current) return;
  
    const camera = cameraRef.current;
    const controls = controlsRef.current;
  
    const direction = new THREE.Vector3(0, 0, -1);
    direction.applyQuaternion(camera.quaternion);
  
    const raycaster = new THREE.Raycaster(camera.position, direction);
  
    // Collect meshes to test
    const meshes: THREE.Mesh[] = [];
  
    const tryAddMesh = (object: any) => {
      // ensure it looks like a mesh and is visible
      if (!object || !("isMesh" in object) || !(object as any).isMesh || !object.visible) return;
  
      const mesh = object as THREE.Mesh;
      const geom = mesh.geometry as THREE.BufferGeometry | undefined | null;
      if (!geom) return;
  
      // Ensure bounding sphere exists; compute one if missing (safe operation)
      if (!geom.boundingSphere) {
        try {
          geom.computeBoundingSphere();
        } catch (e) {
          // computeBoundingSphere may fail for malformed geometry — skip if it does
          return;
        }
      }
  
      const radius = geom.boundingSphere?.radius;
      // Only add large-enough meshes on mobile; on desktop we add all meshes elsewhere
      if (typeof radius === "number") {
        if (isMobileRef.current) {
          if (radius > 1) meshes.push(mesh);
        } else {
          meshes.push(mesh);
        }
      } else if (!isMobileRef.current) {
        // If radius is not available but we're on desktop, still add the mesh as a fallback
        meshes.push(mesh);
      }
    };
  
    if (isMobileRef.current) {
      const maxMeshes = lowMemoryModeRef.current ? 10 : 20;
      let count = 0;
      sceneRef.current.traverse((object: any) => {
        if (count >= maxMeshes) return;
        // tryAddMesh will early-return if not suitable
        const prevLen = meshes.length;
        tryAddMesh(object);
        if (meshes.length > prevLen) count++;
      });
    } else {
      sceneRef.current.traverse((object: any) => {
        // On desktop we keep more meshes; tryAddMesh will add even if radius is undefined
        tryAddMesh(object);
      });
    }
  
    if (meshes.length === 0) return;
  
    const intersects = raycaster.intersectObjects(meshes, false);
  
    if (intersects.length > 0) {
      const intersection = intersects[0];
      if (!intersection) return;
  
      const distanceToObject = intersection.distance;
      const distanceToTarget = camera.position.distanceTo(controls.target);
      const closeToWall = distanceToObject < 2.0;
  
      if (closeToWall || distanceToObject < distanceToTarget * 0.8) {
        const newTargetDistance = Math.min(distanceToObject * 0.8, distanceToTarget);
        const newTarget = camera.position.clone().add(direction.clone().multiplyScalar(newTargetDistance));
  
        let dampingFactor = 0.07;
        if (closeToWall) dampingFactor = 0.04;
  
        controls.target.lerp(newTarget, dampingFactor);
        controls.update();
      }
    }
  };
  

  const adjustControlsBasedOnZoomLevel = () => {
    if (!cameraRef.current || !controlsRef.current || !modelCenterRef.current) return;
    
    // Skip frequent adjustments on low-end devices to save CPU
    if (isMobileRef.current && lowMemoryModeRef.current && Math.random() > 0.3) return;
  
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const modelCenter = modelCenterRef.current;
  
    const distanceToModel = camera.position.distanceTo(modelCenter);
    const distanceToTarget = camera.position.distanceTo(controls.target);
  
    let modelSize = 10;
  if (modelRef.current) {
    const box = new THREE.Box3().setFromObject(modelRef.current);
    const size = box.getSize(new THREE.Vector3());
    modelSize = Math.max(size.x, size.y, size.z);
  }

  const isSmallModel = modelSizeRef.current < 5;
  const farZoomThreshold = modelSize * 0.85;
  const midZoomThreshold = modelSize * 0.45;
  const closeZoomThreshold = modelSize * 0.25;
  const veryCloseZoomThreshold = modelSize * 0.15;

  // On mobile, use simplified controls with consistent values
  if (isMobileRef.current) {
    // Use slightly higher values for mobile to make controls more responsive
    if (distanceToModel > midZoomThreshold) {
      controls.rotateSpeed = isSmallModel ? 0.75 : 0.65;
      controls.dampingFactor = 0.16;  // Higher damping for smoother inertia
      controls.panSpeed = 1.5;
      controls.zoomSpeed = 1.5;
    } else {
      controls.rotateSpeed = isSmallModel ? 0.6 : 0.5;
      controls.dampingFactor = 0.2;  // Even higher damping for closer views
      controls.panSpeed = 1.2;
      controls.zoomSpeed = 1.3;
    }
  } else {
    // Desktop version with more granular control
    if (distanceToModel > farZoomThreshold) {
      controls.rotateSpeed = isSmallModel ? 0.7 : 0.6;
      controls.dampingFactor = isSmallModel ? 0.15 : 0.16; // Increased for smoother inertial motion
      controls.panSpeed = isSmallModel ? 1.8 : 1.5;
      controls.zoomSpeed = isSmallModel ? 1.6 : 1.4;
    } 
    else if (distanceToModel > midZoomThreshold) {
      controls.rotateSpeed = isSmallModel ? 0.6 : 0.5;
      controls.dampingFactor = isSmallModel ? 0.17 : 0.18;
      controls.panSpeed = isSmallModel ? 1.5 : 1.2;
      controls.zoomSpeed = isSmallModel ? 1.5 : 1.3;
    }
    else if (distanceToModel > closeZoomThreshold) {
      controls.rotateSpeed = isSmallModel ? 0.5 : 0.4;
      controls.dampingFactor = isSmallModel ? 0.19 : 0.2;
      controls.panSpeed = isSmallModel ? 1.3 : 1.0;
      controls.zoomSpeed = isSmallModel ? 1.4 : 1.2;
    }
    else if (distanceToModel > veryCloseZoomThreshold) {
      controls.rotateSpeed = isSmallModel ? 0.4 : 0.35;
      controls.dampingFactor = isSmallModel ? 0.22 : 0.25;
      controls.panSpeed = isSmallModel ? 1.0 : 0.8;
      controls.zoomSpeed = isSmallModel ? 1.2 : 1.0;
    }
    else {
      controls.rotateSpeed = isSmallModel ? 0.3 : 0.25;
      controls.dampingFactor = isSmallModel ? 0.25 : 0.28;
      controls.panSpeed = isSmallModel ? 0.8 : 0.6;
      controls.zoomSpeed = isSmallModel ? 1.0 : 0.8;
    }
  }
  
    // Enhanced linear camera rotation when very close to ground/target
    if (distanceToTarget < closeZoomThreshold * (isSmallModel ? 0.4 : 0.3)) {
      const direction = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
      const newTargetDistance = Math.max(distanceToTarget, isSmallModel ? 0.4 : 0.6);
      const newTarget = camera.position.clone().add(direction.multiplyScalar(newTargetDistance));
      controls.target.lerp(newTarget, isSmallModel ? 0.08 : 0.06);
    }
  };

  // Function to change the model
  const changeModel = (modelPath: string, buttonId: string, variant: 'roof' | 'first' | 'ground') => {
    const now = Date.now();
    // Prevent rapid clicking (debounce by 800ms)
    if (now - lastModelChangeTimeRef.current < 800) {
      return;
    }
  
    // If we're already showing this model, don't reload
    if (activeModelButton === buttonId) {
      return;
    }
  
    lastModelChangeTimeRef.current = now;
  
    if (modelChangeTimeoutRef.current) {
      clearTimeout(modelChangeTimeoutRef.current);
    }
  
    modelChangeTimeoutRef.current = window.setTimeout(() => {
      // First, clean up the current scene
      if (rendererRef.current && sceneRef.current) {
        // Dispose materials and geometries
        if (modelRef.current) {
          modelRef.current.traverse((obj: any) => {
            if (obj && obj.isMesh) {
              const mesh = obj as THREE.Mesh;
              if (mesh.geometry) {
                try {
                  mesh.geometry.dispose();
                } catch (e) {
                  // ignore disposal errors
                }
              }
  
              if (Array.isArray(mesh.material)) {
                mesh.material.forEach((m) => {
                  try {
                    m?.dispose?.();
                  } catch (e) {
                    // ignore
                  }
                });
              } else if (mesh.material) {
                try {
                  (mesh.material as any).dispose?.();
                } catch (e) {
                  // ignore
                }
              }
            }
          });
        }
      }
  
      // Force a garbage collection hint (not guaranteed to run)
      const maybeGc = (window as any).gc;
      if (typeof maybeGc === "function") {
        try {
          maybeGc();
        } catch (e) {
          console.log("Manual GC failed or not available:", e);
        }
      } else {
        // no-op: manual GC not available in this environment
      }
  
      setIsLoading(true);
      setCurrentModelPath(modelPath);
      setActiveModelButton(buttonId);
      setCurrentVariant(variant);
      modelChangeTimeoutRef.current = null;
    }, 50); // Small delay to prevent potential race conditions
  };
  

  // Function to optimize model for low-end devices
  const optimizeModelForLowEnd = (model: THREE.Group) => {
    if (!model || modelGeometryOptimizedRef.current) return model;
    
    // Only apply these optimizations to low-end devices
    if (!lowMemoryModeRef.current) return model;
    
    console.log("Optimizing model for low-end device");
    
    // const detailReductionFactor = 0.4; // How much to simplify geometries
    
    model.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        
        // Skip objects with very small geometries
        if (mesh.geometry.boundingSphere && mesh.geometry.boundingSphere.radius < 0.2) {
          mesh.visible = false;
          return;
        }
        
        // Simplify materials
        if (Array.isArray(mesh.material)) {
          mesh.material.forEach((mat) => {
            if (mat.type === 'MeshStandardMaterial') {
              (mat as THREE.MeshStandardMaterial).roughness = 0.8;
              (mat as THREE.MeshStandardMaterial).metalness = 0.1;
            }
          });
        } else if (mesh.material) {
          if (mesh.material.type === 'MeshStandardMaterial') {
            (mesh.material as THREE.MeshStandardMaterial).roughness = 0.8;
            (mesh.material as THREE.MeshStandardMaterial).metalness = 0.1;
          }
        }
        
        // Simplify the geometry - only for less important objects
        if (!isTargetMesh(child.name) && 
            !child.name.includes('main') && 
            !child.name.includes('wall') && 
            !child.name.toLowerCase().includes('floor')) {
            
          mesh.frustumCulled = true;
          
          if (Math.random() > 0.5) {
            // Randomly hide 50% of non-essential objects
            mesh.visible = false;
          }
        }
      }
    });
    
    modelGeometryOptimizedRef.current = true;
    return model;
  };

  const setupOptimizedLights = (scene: THREE.Scene) => {
    // Clear existing lights
    scene.children.forEach(child => {
      if (
        child instanceof THREE.AmbientLight ||
        child instanceof THREE.DirectionalLight ||
        child instanceof THREE.PointLight ||
        child instanceof THREE.HemisphereLight
      ) {
        scene.remove(child);
      }
    });
    
    // Common base lights for all devices - INCREASED INTENSITY FOR MOBILE
    const ambientLight = new THREE.AmbientLight(
      0xfff6ef, 
      isMobileRef.current ? 0.65 : 0.50
    ); // Higher intensity for mobile
    scene.add(ambientLight);
  
    if (devicePerformanceRef.current === 'low') {
      
      const mainLight = new THREE.DirectionalLight(0xffecd6, isMobileRef.current ? 1.15 : 0.85); // Increased for mobile
      mainLight.position.set(15, 20, 15);
      mainLight.castShadow = false; // Disable shadows on low-end devices
      directionalLightRef.current = mainLight;
      scene.add(mainLight);
      
      // Stronger ambient to make model brighter - even stronger on mobile
      const extraAmbient = new THREE.AmbientLight(
        0xfffbf6, 
        isMobileRef.current ? 0.85 : 0.65
      ); // Increased for mobile
      scene.add(extraAmbient);
      
      // Single interior point light with soft warm glow - increased intensity for mobile
      const interiorLight = new THREE.PointLight(
        0xffefe3, 
        isMobileRef.current ? 1.15 : 0.85, 
        40, 
        1.5
      ); // Increased for mobile
      interiorLight.position.set(0, 5, 0);
      scene.add(interiorLight);
      warmLightRef.current = interiorLight;
      
    } else if (devicePerformanceRef.current === 'medium') {
      // Medium device - balance between quality and performance
      
      // Main directional warm light with medium intensity - boosted for mobile
      const mainLight = new THREE.DirectionalLight(
        0xffecd6, 
        isMobileRef.current ? 1.05 : 0.75
      ); // Increased for mobile
      mainLight.position.set(15, 20, 15);
      // Disable shadows on all mobile devices, regardless of performance level
      mainLight.castShadow = !isMobileRef.current;
      
      if (!isMobileRef.current) { // Only configure shadows for desktop
        mainLight.shadow.mapSize.width = 1024;
        mainLight.shadow.mapSize.height = 1024;
        mainLight.shadow.camera.near = 0.5;
        mainLight.shadow.camera.far = 100;
        mainLight.shadow.camera.left = -25;
        mainLight.shadow.camera.right = 25;
        mainLight.shadow.camera.top = 25;
        mainLight.shadow.camera.bottom = -25;
        mainLight.shadow.bias = -0.0001;
        mainLight.shadow.normalBias = 0.04;
        mainLight.shadow.radius = 3.5;
        mainLight.shadow.blurSamples = 8;
      }
      
      directionalLightRef.current = mainLight;
      scene.add(mainLight);
      
      // Gentle warm fill directional with increased intensity - even more for mobile
      const fillLight = new THREE.DirectionalLight(
        0xfff3e8, 
        isMobileRef.current ? 0.7 : 0.5
      ); // Increased for mobile
      fillLight.castShadow = false;
      fillLight.position.set(-15, 10, -15);
      scene.add(fillLight);
      
      // Light hemisphere - increased intensity for mobile
      const hemiLight = new THREE.HemisphereLight(
        0xfffbf6, 
        0xffecd3, 
        isMobileRef.current ? 0.5 : 0.35
      ); // Increased for mobile
      scene.add(hemiLight);
      
      // Central warm point light - increased intensity for mobile
      const warmPoint = new THREE.PointLight(
        0xffc69a, 
        isMobileRef.current ? 0.75 : 0.55, 
        45
      ); // Increased for mobile
      warmPoint.position.set(0, 5, 0);
      warmPoint.castShadow = false;
      scene.add(warmPoint);
      warmLightRef.current = warmPoint;
      
      // Additional interior lights - increased intensity for mobile
      const interiorLight = new THREE.PointLight(
        0xffefe3, 
        isMobileRef.current ? 0.95 : 0.70, 
        45
      ); // Increased for mobile
      interiorLight.position.set(0, 5, 0);
      scene.add(interiorLight);
      
    } else {
      // High-end device - full quality lighting with enhanced brightness
      
      // Main directional warm light with adjusted intensity - boosted for mobile
      const mainLight = new THREE.DirectionalLight(
        0xffecd6, 
        isMobileRef.current ? 1.05 : 0.75
      ); // Increased for mobile
      mainLight.position.set(15, 20, 15);
      
      // Disable shadows on all mobile devices, regardless of performance level
      mainLight.castShadow = !isMobileRef.current;
      
      if (!isMobileRef.current) { // Only configure shadows for desktop
        mainLight.shadow.mapSize.width = 2048;
        mainLight.shadow.mapSize.height = 2048;
        mainLight.shadow.camera.near = 0.5;
        mainLight.shadow.camera.far = 100;
        mainLight.shadow.camera.left = -25;
        mainLight.shadow.camera.right = 25;
        mainLight.shadow.camera.top = 25;
        mainLight.shadow.camera.bottom = -25;
        mainLight.shadow.bias = -0.0001;
        mainLight.shadow.normalBias = 0.04;
        mainLight.shadow.radius = 3.5;
        mainLight.shadow.blurSamples = 16;
      }
      
      directionalLightRef.current = mainLight;
      scene.add(mainLight);
  
      // Gentle warm fill directional with increased intensity - more for mobile
      const fillLight = new THREE.DirectionalLight(
        0xfff3e8, 
        isMobileRef.current ? 0.7 : 0.5
      ); // Increased for mobile
      fillLight.position.set(-15, 10, -15);
      fillLight.castShadow = false;
      scene.add(fillLight);
  
      // Additional fill light from another angle - boosted for mobile
      const fillLight2 = new THREE.DirectionalLight(
        0xfff6ef, 
        isMobileRef.current ? 0.6 : 0.4
      ); // Increased for mobile
      fillLight2.position.set(15, 10, -15);
      fillLight2.castShadow = false;
      scene.add(fillLight2);
  
      // Increased hemisphere light - even more for mobile
      const hemiLight = new THREE.HemisphereLight(
        0xfffbf6, 
        0xffecd3, 
        isMobileRef.current ? 0.5 : 0.35
      ); // Increased for mobile
      scene.add(hemiLight);
  
      // Increased interior ambient - much brighter for mobile
      const interiorAmbient = new THREE.AmbientLight(
        0xfffbf6, 
        isMobileRef.current ? 0.4 : 0.25
      ); // Increased for mobile
      scene.add(interiorAmbient);
  
      // Central warm point light with increased intensity - even higher for mobile
      const warmPoint = new THREE.PointLight(
        0xffc69a, 
        isMobileRef.current ? 0.8 : 0.55, 
        45
      ); // Increased for mobile
      warmPoint.position.set(0, 5, 0);
      warmPoint.castShadow = false;
      scene.add(warmPoint);
      warmLightRef.current = warmPoint;
  
      // Increased intensity for interior lights - boosted for mobile
      const interiorLight = new THREE.PointLight(
        0xffefe3, 
        isMobileRef.current ? 1.0 : 0.7, 
        45
      ); // Increased for mobile
      interiorLight.position.set(0, 5, 0);
      interiorLight.castShadow = false;
      scene.add(interiorLight);
  
      const interiorLight2 = new THREE.PointLight(
        0xfff3e8, 
        isMobileRef.current ? 0.7 : 0.5, 
        40
      ); // Increased for mobile
      interiorLight2.position.set(0, 2, -5);
      interiorLight2.castShadow = false;
      scene.add(interiorLight2);
  
      // Increased warm accents on inner walls - brighter for mobile
      const innerWallLight1 = new THREE.PointLight(
        0xffead0, 
        isMobileRef.current ? 0.5 : 0.35, 
        20
      ); // Increased for mobile
      innerWallLight1.position.set(-3, 3, 0);
      scene.add(innerWallLight1);
  
      const innerWallLight2 = new THREE.PointLight(
        0xffead0, 
        isMobileRef.current ? 0.5 : 0.35, 
        20
      ); // Increased for mobile
      innerWallLight2.position.set(3, 3, 0);
      scene.add(innerWallLight2);
  
      const innerWallLight3 = new THREE.PointLight(
        0xffead0, 
        isMobileRef.current ? 0.5 : 0.35, 
        20
      ); // Increased for mobile
      innerWallLight3.position.set(0, 3, -3);
      scene.add(innerWallLight3);
    }
  };
  // Fix the initial model loading issue
  useEffect(() => {
    // Detect device capabilities first
    const { isMobile, performance } = detectDeviceCapabilities();
    
    const container = containerRef.current;
    if (!container) return;

    targetMeshesRef.current.clear();
    originalMaterialsRef.current.clear();
    unitMeshesRef.current.clear();
    currentlyHighlightedUnitRef.current = null;
    setHoveredMeshName(null);
    userInteractedRef.current = false;
    autoRotateRef.current = true;
    enableHoverCheckingRef.current = false;
    modelGeometryOptimizedRef.current = false;

    if (!sceneRef.current) {
      const scene = new THREE.Scene();
      sceneRef.current = scene;
      // Change from gray to a warm background like in IndividualModelViewer
      scene.background = new THREE.Color(0xfff8f5); // Warm white/cream background
    } else {
      // Clear existing scene but keep the reference
      while (sceneRef.current.children.length > 0) {
        const child = sceneRef.current.children[0];
        if (child) {
          sceneRef.current.remove(child);
        }
      }
    }

    const scene = sceneRef.current;

    // Create or reuse camera
    if (!cameraRef.current) {
      const camera = new THREE.PerspectiveCamera(
        isMobile ? 45 : 40, // Further increased from 40 to 45 for mobile to show even more of the scene
        container.clientWidth / container.clientHeight,
        0.1,
        1000
      );
      camera.position.set(5, 11, 15);
      cameraRef.current = camera;
    } else {
      cameraRef.current.aspect = container.clientWidth / container.clientHeight;
      cameraRef.current.updateProjectionMatrix();
    }
    
    const camera = cameraRef.current;

    // Create or reuse renderer with optimizations for mobile
    if (!rendererRef.current) {
      const renderer = new THREE.WebGLRenderer({
        antialias: !isMobile || performance !== 'low', // Disable antialiasing for low-end mobile
        powerPreference: isMobile ? 'low-power' : 'high-performance',
        precision: isMobile && performance === 'low' ? 'lowp' : 'mediump', // Use lowp for low-end devices
        alpha: !isMobile || performance !== 'low', // Disable alpha for low-end mobile
        stencil: !isMobile, // Disable stencil for mobile
        depth: true, // Always keep depth buffer
        logarithmicDepthBuffer: false // Disable logarithmic depth buffer for performance
      });
      
      rendererRef.current = renderer;
      const dracoLoader = initDracoLoader();
      const loader = new GLTFLoader();
      loader.setDRACOLoader(dracoLoader);
      
      // Lower pixel ratio for mobile devices to improve performance
      let pixelRatio = 1.0;
      if (!isMobile) {
        pixelRatio = Math.min(window.devicePixelRatio, 1.5);
      } else if (performance === 'medium') {
        pixelRatio = Math.min(window.devicePixelRatio, 1.0);
      } else if (performance === 'low') {
        pixelRatio = 0.75; // Very low pixel ratio for low-end mobile
      }
      
      renderer.setPixelRatio(pixelRatio);
      
      // Only enable shadows on non-mobile devices
      renderer.shadowMap.enabled = !isMobileRef.current;
      if (renderer.shadowMap.enabled) {
        renderer.shadowMap.type = performance === 'high' ? 
          THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
      }
      
      // Updated tone mapping to match IndividualModelViewer
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = isMobileRef.current ? 1.8 : 1.5; 
      
      container.appendChild(renderer.domElement);
    }
    
    const renderer = rendererRef.current;
    renderer.setSize(container.clientWidth, container.clientHeight);

    // Create or reuse controls - FIXED CONTROLS INITIALIZATION
    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }
    
    const controls = new OrbitControls(camera, renderer.domElement);
    controlsRef.current = controls;
    
    // Make sure controls are properly initialized before model loading
    controls.enableDamping = true;
    controls.dampingFactor = isMobile ? 0.16 : 0.15;  // Higher values = more inertia/momentum
    controls.screenSpacePanning = true;
    controls.maxPolarAngle = Math.PI / 2.15;  
    controls.minPolarAngle = 0.18;           
    controls.minDistance = 0.5;
    controls.maxDistance = 18.0;
    controls.zoomSpeed = isMobile ? 1.2 : 1.0;  // Lower speeds for smoother movement
    controls.rotateSpeed = isMobile ? 0.55 : 0.45;  // Lower for smoother rotation
    controls.enableZoom = true;
    controls.panSpeed = isMobile ? 1.2 : 1.0;        // Added for consistent keyboard panning
    
    
    controls.mouseButtons = {
      LEFT: THREE.MOUSE.ROTATE,
      MIDDLE: THREE.MOUSE.DOLLY,
      RIGHT: THREE.MOUSE.PAN
    };
    
    // Adjust touch controls for mobile
    if (isMobile) {
      controls.touches = {
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN
      };
    }
    
    // Function to enforce panning boundaries with proper horizontal and vertical limits
    const enforcePanningBoundaries = () => {
      if (!controlsRef.current || !modelCenterRef.current || !modelBoundsRef.current) return;
      
      const target = controlsRef.current.target;
      const modelCenter = modelCenterRef.current;
      const bounds = modelBoundsRef.current;
      const modelSize = bounds.getSize(new THREE.Vector3());
      const isSmallModel = modelSizeRef.current < 5;
      
      // Calculate pan limits based on model size - more restrictive
      let panLimitFactor = isSmallModel ? 1.5 : 1.0;
      
      // Horizontal limits (X and Z axes) - tighter constraints
      const maxXOffset = modelSize.x * panLimitFactor;
      const maxZOffset = modelSize.z * panLimitFactor;
      
      // Vertical limits (Y axis) - simplified to only prevent going below ground
      // Similar to IndividualModelViewer
      const minY = groundPlaneRef.current ? groundPlaneRef.current.position.y : -0.1;
      
      // Apply horizontal constraints (X axis)
      if (target.x < modelCenter.x - maxXOffset) {
        target.x = THREE.MathUtils.lerp(target.x, modelCenter.x - maxXOffset, 0.1);
      } else if (target.x > modelCenter.x + maxXOffset) {
        target.x = THREE.MathUtils.lerp(target.x, modelCenter.x + maxXOffset, 0.1);
      }
      
      // Apply horizontal constraints (Z axis)
      if (target.z < modelCenter.z - maxZOffset) {
        target.z = THREE.MathUtils.lerp(target.z, modelCenter.z - maxZOffset, 0.1);
      } else if (target.z > modelCenter.z + maxZOffset) {
        target.z = THREE.MathUtils.lerp(target.z, modelCenter.z + maxZOffset, 0.1);
      }
      
      // Apply vertical constraints (Y axis) - only prevent going below ground
      if (target.y < minY) {
        target.y = minY;
      }
      
      // Additional radial constraint for overall distance from center
      const targetDelta = new THREE.Vector3().subVectors(target, modelCenter);
      // Only apply horizontal distance (ignore Y for this check)
      const horizontalDelta = new THREE.Vector3(targetDelta.x, 0, targetDelta.z);
      const horizontalDistance = horizontalDelta.length();
      
      const maxRadialDistance = Math.max(maxXOffset, maxZOffset) * 1.0;
      
      if (horizontalDistance > maxRadialDistance) {
        horizontalDelta.normalize().multiplyScalar(maxRadialDistance);
        const constrainedTarget = modelCenter.clone().add(horizontalDelta);
        constrainedTarget.y = target.y; // Keep the Y value as it's already constrained
        target.lerp(constrainedTarget, 0.1);
      }
    };
    
    controls.addEventListener('start', () => {
      userInteractedRef.current = true;
      autoRotateRef.current = false;
      enableHoverCheckingRef.current = true;
    });
    
    controls.addEventListener('change', () => {
      enforcePanningBoundaries();
      if (cameraRef.current && controlsRef.current) {
        // On mobile low-end devices, throttle control adjustments
        if (!isMobile || !lowMemoryModeRef.current || Math.random() > 0.7) {
          adjustControlsBasedOnZoomLevel();
        }
      }
    });
    
    // Ensure controls are active and working from the start
    controls.enabled = true;
    
    // Update controls to make sure they're active
    controls.update();

    // Setup optimized lighting based on device capability
    setupOptimizedLights(scene);

    // Initialize highlight material with warmer highlight color
    const highlightMaterial = new THREE.MeshStandardMaterial({
      color: 0xFFD600,
      emissive: 0xFFC107,
      emissiveIntensity: 0.35,
      roughness: 0.7,
      metalness: 0.15,
      transparent: true,
      opacity: 0.45,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
      side: THREE.FrontSide,
      depthWrite: false,
      depthTest: true,
      blending: THREE.NormalBlending
    });
    highlightMaterialRef.current = highlightMaterial;

    const cleanupMouseEvents = setupMouseEvents();

    // Use optimized DRACO loader
    const dracoLoader = new DRACOLoader();
    // Fix DRACO loader path to ensure it's correctly loaded
    dracoLoader.setDecoderPath('https://unpkg.com/three@0.159.0/examples/jsm/libs/draco/');
    
    // Force JS decoder for better compatibility with mobile
    dracoLoader.setDecoderConfig({ type: 'js' });

    const loader = new GLTFLoader();
    loader.setDRACOLoader(dracoLoader);
    
    // FIXED: Make intro animation shorter and ensure controls work during/after it
    let introAnimation = true;
    let animationStartTime = 0;
    //const animationDuration = isMobile ? 800 : 1000; // Shorter on mobile
    
    // Initialize animation frame reference early
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    const loadManager = new THREE.LoadingManager();
    loadManager.onProgress = (_, loaded, total) => {
      if (total > 0) {
        const progress = Math.min(Math.floor((loaded / total) * 100), 100);
        setLoadingProgress(progress);
      }
    };
    
    loader.manager = loadManager;
    
    // Start a simple animation while waiting for the model to load
    // This ensures the renderer and controls are active
    const preloadAnimate = () => {
      if (rendererRef.current && sceneRef.current && cameraRef.current && controlsRef.current) {
        controlsRef.current.update();
        rendererRef.current.render(sceneRef.current, cameraRef.current);
      }
      
      if (isLoading) {
        animationFrameRef.current = requestAnimationFrame(preloadAnimate);
      }
    };
    
    preloadAnimate();
    
    // Improved error handling and retry mechanism for model loading
   // Load model function with fixes to prevent bouncing
const loadModel = (retryCount = 0) => {
  const maxRetries = 2;
  
  // Clear previous model if it exists
  if (modelRef.current && sceneRef.current) {
    sceneRef.current.remove(modelRef.current);
    modelRef.current = null;
  }
  
  console.log(`Loading model (attempt ${retryCount + 1}): ${currentModelPath}`);
  
  loader.load(
    currentModelPath,
    (gltf: GLTF) => {
      console.log("Model loaded successfully:", currentModelPath);
      
      // Check screen width and height once for use throughout model setup
      const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
      const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
      const isMobileLandscape = isMobileRef.current && screenWidth > screenHeight;
      
      // Remove previous model if it exists
      if (modelRef.current && sceneRef.current) {
        sceneRef.current.remove(modelRef.current);
        modelRef.current = null;
      }
      
      const model = gltf.scene;
      
      // Apply optimizations for low-end devices
      if (isMobileRef.current && lowMemoryModeRef.current) {
        optimizeModelForLowEnd(model);
      }
      
      modelRef.current = model;

      // Center the model at origin - removed right offset for proper centering
      console.log(`Centering model at origin (isMobile: ${isMobile}, screenWidth: ${screenWidth})`);

      const box = new THREE.Box3().setFromObject(model);
      modelBoundsRef.current = box.clone();
      
      const center = new THREE.Vector3();
      box.getCenter(center);
      
      // Determine model size
      const size = box.getSize(new THREE.Vector3());
      const modelDiagonal = size.length();
      modelSizeRef.current = modelDiagonal;
      
      console.log(`Model size: ${modelDiagonal}`);
      
      // Position model - Center the model at origin
      model.position.sub(center);
      
      const baseScale = 12 / modelDiagonal;
      // Adjust zoom based on mobile orientation - bigger for landscape, smaller for portrait
      const extraZoom = (isMobile || screenWidth <= 768) 
        ? (isMobileLandscape ? 1.4 : 1.0) // Bigger scale for landscape (1.4), smaller for portrait (1.0)
        : 1.8; // Desktop scale
      const scale = baseScale * extraZoom;
      model.scale.set(scale, scale, scale);
      
      model.rotation.y = 0;
      
      // Center model at origin (0, 0, 0)
      model.position.set(0, 0, 0);
      console.log(`Model positioned at: (${model.position.x}, ${model.position.y}, ${model.position.z})`);
      
      // Set model center reference to origin
      const modelCenter = new THREE.Vector3(0, 0, 0);
      modelCenterRef.current = modelCenter.clone();

      if (!sceneRef.current) return;
      
      // Add model to scene AFTER transformations are applied
      scene.add(model);
      
      if (groundPlaneRef.current && sceneRef.current.getObjectById(groundPlaneRef.current.id)) {
        sceneRef.current.remove(groundPlaneRef.current);
        groundPlaneRef.current = null;
      }
      const groundGeometry = new THREE.PlaneGeometry(200, 200);
      const groundMaterial = new THREE.ShadowMaterial({ 
        opacity: devicePerformanceRef.current === 'low' ? 0.03 : 0.05,
        color: 0x000000 
      });
      const groundPlane = new THREE.Mesh(groundGeometry, groundMaterial);
      groundPlane.rotation.x = -Math.PI / 2;
      groundPlane.position.y = -0.1;
      groundPlane.receiveShadow = !isMobileRef.current && devicePerformanceRef.current !== 'low';
      sceneRef.current.add(groundPlane);
      groundPlaneRef.current = groundPlane;

      targetMeshesRef.current.clear();
      originalMaterialsRef.current.clear();
      unitMeshesRef.current.clear();
      
      model.traverse((child: THREE.Object3D) => {
        if ((child as THREE.Mesh).isMesh) {
          const mesh = child as THREE.Mesh;
          
          mesh.castShadow = !isMobileRef.current && devicePerformanceRef.current !== 'low';
          mesh.receiveShadow = !isMobileRef.current && devicePerformanceRef.current !== 'low';
          mesh.frustumCulled = true;
          
          // Optimize materials for current device
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((mat) => {
              if (mat.isMaterial) {
                mat.polygonOffset = true;
                mat.polygonOffsetFactor = 0.1;
                mat.polygonOffsetUnits = 0.1;
                
                // Simplify material properties on low-end devices
                if (devicePerformanceRef.current === 'low' && (mat as any).roughness !== undefined) {
                  (mat as any).roughness = 0.8; // Higher roughness = simpler shading
                  (mat as any).metalness = 0.1; // Lower metalness = simpler reflection
                }
              }
            });
          } else if (mesh.material && mesh.material.isMaterial) {
            mesh.material.polygonOffset = true;
            mesh.material.polygonOffsetFactor = 0.1;
            mesh.material.polygonOffsetUnits = 0.1;
            
            // Simplify material properties on low-end devices
            if (devicePerformanceRef.current === 'low' && (mesh.material as any).roughness !== undefined) {
              (mesh.material as any).roughness = 0.8;
              (mesh.material as any).metalness = 0.1;
            }
          }
          
          // Process target meshes
          if (isTargetMesh(child.name) && !['Cars', 'human', 'Site'].includes(child.name)) {
            targetMeshesRef.current.set(child.name, mesh);
            originalMaterialsRef.current.set(child.uuid, mesh.material);
            
            // Group by unit number
            const unitNumber = getUnitNumberFromName(child.name);
            if (unitNumber) {
              if (!unitMeshesRef.current.has(unitNumber)) {
                unitMeshesRef.current.set(unitNumber, []);
              }
              unitMeshesRef.current.get(unitNumber)?.push(mesh);
            }
          }
          
          // Optimize geometry
          if (mesh.geometry) {
            mesh.geometry.computeBoundingBox();
            mesh.geometry.computeBoundingSphere();
          }
          
          // Convert basic materials to standard materials with warm parameters
          // But use simpler materials on low-end devices
          if (devicePerformanceRef.current === 'low') {
            // Use MeshLambertMaterial for low-end devices (faster than MeshStandardMaterial)
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((mat, index) => {
                if ((mat as THREE.MeshBasicMaterial).isMeshBasicMaterial) {
                  const basicMat = mat as THREE.MeshBasicMaterial;
                  const newMat = new THREE.MeshLambertMaterial({
                    color: basicMat.color,
                    map: basicMat.map,
                    transparent: basicMat.transparent,
                    opacity: basicMat.opacity
                  });
                  (mesh.material as THREE.Material[])[index] = newMat;
                } else if ((mat as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
                  // Convert any standard materials to lambert on low-end
                  const stdMat = mat as THREE.MeshStandardMaterial;
                  const newMat = new THREE.MeshLambertMaterial({
                    color: stdMat.color,
                    map: stdMat.map,
                    transparent: stdMat.transparent,
                    opacity: stdMat.opacity,
                    emissive: stdMat.emissive,
                    emissiveMap: stdMat.emissiveMap
                  });
                  (mesh.material as THREE.Material[])[index] = newMat;
                }
              });
            } else if ((mesh.material as THREE.MeshBasicMaterial).isMeshBasicMaterial) {
              const basicMat = mesh.material as THREE.MeshBasicMaterial;
              const newMat = new THREE.MeshLambertMaterial({
                color: basicMat.color,
                map: basicMat.map,
                transparent: basicMat.transparent,
                opacity: basicMat.opacity
              });
              mesh.material = newMat;
            } else if ((mesh.material as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
              const stdMat = mesh.material as THREE.MeshStandardMaterial;
              const newMat = new THREE.MeshLambertMaterial({
                color: stdMat.color,
                map: stdMat.map,
                transparent: stdMat.transparent,
                opacity: stdMat.opacity,
                emissive: stdMat.emissive,
                emissiveMap: stdMat.emissiveMap
              });
              mesh.material = newMat;
            }
          } else {
            // For medium/high devices, use standard materials
            if (Array.isArray(mesh.material)) {
              mesh.material.forEach((mat, index) => {
                if ((mat as THREE.MeshBasicMaterial).isMeshBasicMaterial) {
                  const basicMat = mat as THREE.MeshBasicMaterial;
                  const newMat = new THREE.MeshStandardMaterial({
                    color: basicMat.color,
                    map: basicMat.map,
                    transparent: basicMat.transparent,
                    opacity: basicMat.opacity,
                    roughness: 0.7,
                    metalness: 0.0
                  });
                  (mesh.material as THREE.Material[])[index] = newMat;
                }
              });
            } else if ((mesh.material as THREE.MeshBasicMaterial).isMeshBasicMaterial) {
              const basicMat = mesh.material as THREE.MeshBasicMaterial;
              const newMat = new THREE.MeshStandardMaterial({
                color: basicMat.color,
                map: basicMat.map,
                transparent: basicMat.transparent,
                opacity: basicMat.opacity,
                roughness: 0.7,
                metalness: 0.0
              });
              mesh.material = newMat;
            }
          }
        }
      });

      // Calculate bounding sphere after all transformations (including offset)
      const boundingBox = new THREE.Box3().setFromObject(model);
      modelBoundsRef.current = boundingBox.clone(); // Update bounds after offset
      
      // Model center is already set above after positioning - use it here
      if (!modelCenterRef.current) return;
      
      // Ensure model position is still set correctly after all transformations
      if (Math.abs(model.position.x) > 0.01 || Math.abs(model.position.y) > 0.01 || Math.abs(model.position.z) > 0.01) {
        console.log(`Re-centering model: model position was (${model.position.x}, ${model.position.y}, ${model.position.z}), setting to (0, 0, 0)`);
        model.position.set(0, 0, 0);
      }
      
      const boundingSphere = new THREE.Sphere();
      boundingBox.getBoundingSphere(boundingSphere);
      
      // Center camera setup - always look at origin where model is centered
      const origin = new THREE.Vector3(0, 0, 0);
      
      // Always look at origin (model center) for proper centering
      const cameraTarget = origin.clone();
      
      controls.target.copy(cameraTarget);
      console.log(`Controls target set to: (${controls.target.x}, ${controls.target.y}, ${controls.target.z})`);
      
      const radius = boundingSphere.radius;
      
      // Adjust camera distance based on mobile orientation - closer for landscape
      const isSmallScreen = isMobile || screenWidth <= 768;
      let heightFactor = isSmallScreen ? 0.7 : 0.55;
      let distanceFactor = isSmallScreen 
        ? (isMobileLandscape ? 1.8 : 3.8)
        : 1.65;
      
      // Set initial camera position - always relative to origin (model center)
      camera.position.set(
        origin.x,
        origin.y + radius * heightFactor,
        origin.z + radius * distanceFactor
      );
      console.log(`Camera position set to: (${camera.position.x}, ${camera.position.y}, ${camera.position.z})`);
      
      // Store initial position for reset function
      initialCameraPositionRef.current = camera.position.clone();
      
      // Set target position with minimal difference from initial
      let targetHeightFactor = heightFactor - 0.05;
      let targetDistanceFactor = distanceFactor - 0.12;

      const targetCameraPosition = new THREE.Vector3(
        origin.x,
        origin.y + radius * targetHeightFactor,
        origin.z + radius * targetDistanceFactor
      );
      
      // Update initialTargetRef to use the camera target
      initialTargetRef.current = cameraTarget.clone();

      // Position the lights based on model size - centered at origin
      if (directionalLightRef.current) {
        const light = directionalLightRef.current;
        
        // Position the main directional light for warm lighting
        light.position.set(
          radius * 1.5, 
          radius * 2.2, 
          radius * 1.5
        );
        light.intensity = devicePerformanceRef.current === 'low' ? 2.0 : 1.8;
        
        const targetObject = new THREE.Object3D();
        targetObject.position.copy(origin);
        scene.add(targetObject);
        light.target = targetObject;
      }
      
      // Reposition warm accent lights - centered at origin
      if (warmLightRef.current) {
        warmLightRef.current.position.set(
          0,
          boundingSphere.radius * 0.6,
          0
        );
        warmLightRef.current.distance = boundingSphere.radius * 2.5;
      }
      
      // Make sure controls are updated with the new target
      controls.update();
      console.log(`After controls.update() - target: (${controls.target.x}, ${controls.target.y}, ${controls.target.z}), camera: (${camera.position.x}, ${camera.position.y}, ${camera.position.z})`);
      
      // Final verification: ensure model position is correct before starting animation
      if (modelRef.current) {
        if (Math.abs(modelRef.current.position.x) > 0.01 || Math.abs(modelRef.current.position.y) > 0.01 || Math.abs(modelRef.current.position.z) > 0.01) {
          console.log(`Final position fix: setting model from (${modelRef.current.position.x}, ${modelRef.current.position.y}, ${modelRef.current.position.z}) to (0, 0, 0)`);
          modelRef.current.position.set(0, 0, 0);
        }
        console.log(`Final model position: (${modelRef.current.position.x}, ${modelRef.current.position.y}, ${modelRef.current.position.z}), center: (0, 0, 0)`);
      }

      // Setup environment map - simplified for mobile
      if (devicePerformanceRef.current !== 'low') {
        const pmremGenerator = new PMREMGenerator(renderer);
        pmremGenerator.compileEquirectangularShader();
        const cubeRenderTarget = pmremGenerator.fromScene(new THREE.Scene());
        scene.environment = cubeRenderTarget.texture;
        pmremGenerator.dispose();
      }

      // Loading complete
      setIsLoading(false);
      introAnimation = true;
      animationStartTime = Date.now();
      
      if (model) {
        model.rotation.y = 0;
      }

      // Handle double-click to reset view
      const handleDoubleClick = () => {
        resetCameraView();
      };
      
      container.addEventListener('dblclick', handleDoubleClick);

      // Calculate frame rate based on device performance
      const getOptimizedFrameRate = () => {
        if (isMobileRef.current) {
          if (devicePerformanceRef.current === 'low') return 30;
          return 45;
        }
        return 60;
      };
      
      const targetFrameRate = getOptimizedFrameRate();
      const frameInterval = 1000 / targetFrameRate;
      let lastFrameTime = 0;

      // Improved animation loop with smoother intro animation
      const animate = (currentTime: number) => {
        if (!sceneRef.current || !modelRef.current || !controlsRef.current) return;
        
        animationFrameRef.current = requestAnimationFrame(animate);
        
        // IMPORTANT: Update controls on every frame for smooth inertia effects
        controlsRef.current.update();
        
        // Rest of the animation function can throttle rendering
        const elapsed = currentTime - lastFrameTime;
        const skipFrame = elapsed < frameInterval;
        
        if (skipFrame && !introAnimation) {
          // Even when skipping frame rendering, we've already updated controls above
          return;
        }
        
        lastFrameTime = currentTime;
        
        // FIX: Improved intro animation with gentler easing
        if (introAnimation) {
          const elapsedIntro = Date.now() - animationStartTime;
          // FIX: Use shorter animation duration to minimize bounce
          const duration = isMobileRef.current ? 600 : 800; 
          const progress = Math.min(elapsedIntro / duration, 1);
          
          // FIX: Use a smoother easing function with less dramatic movement
          const easeOutQuad = (t: number): number => 1 - (1 - t) * (1 - t);
          const easedProgress = easeOutQuad(progress);
          
          // FIX: Apply very subtle movement to avoid bounce
          camera.position.lerpVectors(
            initialCameraPositionRef.current || camera.position.clone(),
            targetCameraPosition,
            easedProgress * 0.8 // Reduce movement magnitude by 20%
          );
        
          if (progress >= 1) {
            introAnimation = false;
            controls.enabled = true;
            initialCameraPositionRef.current = camera.position.clone();
            autoRotateRef.current = true;
          } else {
            controls.enabled = true;
          }
        } else if (modelRef.current && autoRotateRef.current && !userInteractedRef.current) {
          // much slower auto-rotate for calmer feel
          const rotationSpeed = 0.0004;
          modelRef.current.rotation.y += rotationSpeed;
        }
        
        if (!introAnimation) {
          if (!isMobileRef.current || !lowMemoryModeRef.current || Math.random() < 0.15) {
            adjustControlsBasedOnZoomLevel();
      
            // Skip updateControlTargetBasedOnView for mobile to prevent target from moving away from model center
            if (!isMobileRef.current && Math.random() < 0.01 &&
                controlsRef.current &&
                cameraRef.current &&
                cameraRef.current.position.distanceTo(controlsRef.current.target) > controlsRef.current.minDistance * 1.2) {
              updateControlTargetBasedOnView();
            }
      
            enforcePanningBoundaries();
          }
        }
      
        // Check intersections in animation loop with built-in throttling
      if (!introAnimation && mouseMoveListenerAttachedRef.current && enableHoverCheckingRef.current) {
  // Call checkIntersection - it has built-in debouncing to prevent flickering
  checkIntersection();
}
      
        renderer.render(scene, camera);
      };
      
      
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      // Start the main animation loop
      animate(0);

      return () => {
        container.removeEventListener('dblclick', handleDoubleClick);
      };
    },
    (xhr: ProgressEvent<EventTarget>) => {
      if (xhr.total) {
        const progress = Math.min(Math.floor((xhr.loaded / xhr.total) * 100), 100);
        setLoadingProgress(progress);
      }
    },
    (error: any) => {
      console.error('Model loading error:', error);
      
      if (retryCount < maxRetries) {
        console.log(`Retrying model load (attempt ${retryCount + 2})...`);
        
        if (!lowMemoryModeRef.current) {
          lowMemoryModeRef.current = true;
          devicePerformanceRef.current = 'low';
          console.log("Switched to low memory mode for retry");
          
          if (rendererRef.current) {
            rendererRef.current.shadowMap.enabled = false;
            rendererRef.current.setPixelRatio(0.75);
          }
        }
        
        setTimeout(() => {
          loadModel(retryCount + 1);
        }, 1000);
      } else {
        setIsLoading(false);
        console.error("Failed to load model after multiple attempts");
        
        // Show fallback content or error message
        if (containerRef.current) {
          const errorDiv = document.createElement('div');
          errorDiv.style.position = 'absolute';
          errorDiv.style.top = '50%';
          errorDiv.style.left = '50%';
          errorDiv.style.transform = 'translate(-50%, -50%)';
          errorDiv.style.color = '#e53935';
          errorDiv.style.fontSize = '16px';
          errorDiv.style.textAlign = 'center';
          errorDiv.style.padding = '20px';
          errorDiv.style.backgroundColor = 'rgba(255,255,255,0.9)';
          errorDiv.style.borderRadius = '8px';
          errorDiv.innerHTML = `
            <p>Sorry, we couldn't load the 3D model.</p>
            <p>Please try again or contact support if the problem persists.</p>
            <button id="retryBtn" style="
              background: #e53935; 
              color: white; 
              border: none; 
              padding: 10px 15px; 
              border-radius: 4px; 
              margin-top: 15px;
              cursor: pointer;
            ">Retry</button>
          `;
          containerRef.current.appendChild(errorDiv);
          
          // Add retry button handler
          const retryBtn = document.getElementById('retryBtn');
          if (retryBtn) {
            retryBtn.addEventListener('click', () => {
              if (containerRef.current && errorDiv.parentNode === containerRef.current) {
                containerRef.current.removeChild(errorDiv);
              }
              setIsLoading(true);
              loadModel(0);
            });
          }
        }
      }
    }
  );
};
    
    // Start the initial model loading
    loadModel();

    // Handle window resize with throttling for better performance
    let resizeTimeout: number;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        if (!container) return;
        
        const width = container.clientWidth;
        const height = container.clientHeight;
        
        if (cameraRef.current) {
          cameraRef.current.aspect = width / height;
          cameraRef.current.updateProjectionMatrix();
        }
        
        if (rendererRef.current) {
          rendererRef.current.setSize(width, height);
        }
      }, 100);
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      if (cleanupMouseEvents) cleanupMouseEvents();
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      resetHighlighting();
      
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
      
      if (modelChangeTimeoutRef.current) {
        clearTimeout(modelChangeTimeoutRef.current);
        modelChangeTimeoutRef.current = null;
      }
    };
  }, [currentModelPath, navigate]);

  useEffect(() => {
    const cleanupMouseEvents = setupMouseEvents();
    return cleanupMouseEvents;
  }, [isLoading]);


useEffect(() => {
  if (typeof window === 'undefined') return;
  const onResize = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };
  
  const updateIsMobile = () => {
    const uaMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const hasTouch =
      typeof navigator !== 'undefined' &&
      (('maxTouchPoints' in navigator && navigator.maxTouchPoints > 0) ||
        'ontouchstart' in window);
    setIsMobileDevice(uaMobile || hasTouch);
  };
  
  updateIsMobile();
  window.addEventListener('resize', onResize);
  window.addEventListener('resize', updateIsMobile);
  window.addEventListener('orientationchange', updateIsMobile);
  
  return () => {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('resize', updateIsMobile);
    window.removeEventListener('orientationchange', updateIsMobile);
  };
}, []);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        backgroundColor: '#F5F5F5',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          left: 0
        }}
      />

      {/* Loading */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            zIndex: 10
          }}
        >
          <div className="custom-loader">
            <div className="circle-segment segment-1"></div>
            <div className="circle-segment segment-2"></div>
            <div className="circle-segment segment-3"></div>
            <div className="circle-segment segment-4"></div>
          </div>
          <p style={{
            color: '#333',
            fontSize: '16px',
            fontWeight: '500',
            marginTop: '25px',
            fontFamily: 'Arial, sans-serif'
          }}>
            Loading 3D Model... {loadingProgress}%
          </p>
          <div style={{
            width: '240px',
            height: '6px',
            backgroundColor: '#e0e0e0',
            borderRadius: '3px',
            overflow: 'hidden',
            marginTop: '8px'
          }}>
            <div style={{
              width: `${loadingProgress}%`,
              height: '100%',
              backgroundColor: '#e53935',
              borderRadius: '3px',
              transition: 'width 0.3s ease-in-out'
            }} />
          </div>
          <style>{`
            .custom-loader { position: relative; width: 80px; height: 80px; animation: rotate 2s linear infinite; }
            .circle-segment { position: absolute; width: 45%; height: 45%; border-radius: 50%; background-color: #e53935; }
            .segment-1 { top: 0; left: 0; }
            .segment-2 { top: 0; right: 0; }
            .segment-3 { bottom: 0; right: 0; }
            .segment-4 { bottom: 0; left: 0; }
            @keyframes rotate { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } 
          `}</style>
        </div>
      )}

      {/* Desktop control help */}
      {!isLoading && windowWidth > 768 && !isMobileDevice && (
        <div
          style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            padding: '10px 15px',
            color: '#333333',
            borderRadius: '5px',
            fontSize: '13px',
            fontFamily: 'Arial, sans-serif',
            fontWeight: '500',
            zIndex: 10,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Left-click:</span> Rotate
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Right-click:</span> Pan
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Scroll:</span> Zoom
          </span>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold', marginRight: '5px' }}>Double-click:</span> Reset View
          </span>
        </div>
      )}

      {/* Mobile help button */}
      {!isLoading && isMobileDevice && (
        <div
          style={{
            position: 'absolute',
            bottom: (windowWidth > windowHeight ? '140px' : '220px'),
            right: '20px',
            zIndex: 11,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end'
          }}
        >
          <button
            onClick={() => setShowMobileHelp(s => !s)}
            aria-expanded={showMobileHelp}
            aria-label="Show controls help"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.8)',
              border: '1px solid #ddd',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              cursor: 'pointer'
            }}
          >
            <span style={{ fontSize: '20px' }}>?</span>
          </button>
          {showMobileHelp && (
            <div
              style={{
                flexDirection: 'column',
                backgroundColor: 'rgba(255,255,255,0.95)',
                borderRadius: '8px',
                padding: '12px',
                marginTop: '10px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                border: '1px solid #eee',
                maxWidth: '220px',
                display: 'flex',
                textAlign: 'left'
              }}
            >
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>One finger:</strong> Rotate
              </div>
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>Two fingers:</strong> Pan
              </div>
              <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                <strong>Pinch:</strong> Zoom
              </div>
              <div style={{ fontSize: '12px' }}>
                <strong>Double-tap:</strong> Reset View
              </div>
            </div>
          )}
        </div>
      )}

      {/* Variant buttons in order: Ground, First, Roof */}
      <div
        style={{
          position: 'absolute',
          bottom: windowWidth > 768 ? '20px' : (windowWidth > windowHeight && windowWidth <= 768 ? '40px' : '180px'),
          left: windowWidth > 768 ? '50%' : '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          flexDirection: windowWidth > 768 ? 'row' : 'row',
          gap: windowWidth > 768 ? '28px' : '0',
          zIndex: 10,
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        {(windowWidth > 768 && !isMobileDevice) ? (
          // Desktop: Thumbnail buttons, ORDER: Ground, First, Roof
          [
            {
              key: 'groundFloor',
              thumb: groundFloorThumb,
              label: 'Ground Floor',
              model: '/assets/ground_floor.glb',
              aria: 'View ground floor',
              variant: 'ground' as const
            },
            {
              key: 'firstFloor',
              thumb: firstFloorThumb,
              label: 'First Floor',
              model: '/assets/first_floor.glb',
              aria: 'View first floor',
              variant: 'first' as const
            },
            {
              key: 'roof',
              thumb: ceilingThumb,
              label: 'Roof',
              model: '/assets/ceiling.glb',
              aria: 'View roof',
              variant: 'roof' as const
            }
          ].map(item => {
            const size = 112;
            return (
              <button
                key={item.key}
                onClick={() => changeModel(item.model, item.key, item.variant)}
                aria-pressed={activeModelButton === item.key}
                aria-label={item.aria}
                style={{
                  display: 'block',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  margin: 0,
                  width: `${size}px`,
                  height: `${size}px`
                }}
              >
                <div
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: `2px solid ${activeModelButton === item.key ? '#e53935' : '#ddd'}`,
                    boxShadow: activeModelButton === item.key ? '0 0 8px rgba(229,57,53,0.5)' : '0 2px 5px rgba(0,0,0,0.08)',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: '#f5f5f5',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundImage: `url(${item.thumb})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      filter: activeModelButton === item.key ? 'none' : 'brightness(0.93)'
                    }}
                  />
                  <span
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      width: '100%',
                      padding: '10px 0 2px 0',
                      fontSize: '15px',
                      fontWeight: activeModelButton === item.key ? '600' : '400',
                      color: '#000',
                      fontFamily: 'Arial, sans-serif',
                      textAlign: 'center',
                      userSelect: 'none',
                      border: 'none',
                      background: 'none'
                    }}
                  >
                    {item.label}
                  </span>
                </div>
              </button>
            );
          })
        ) : (
          // Mobile: Segmented control style, ORDER: Ground, First, Roof
          <div
            style={{
              display: 'flex',
              backgroundColor: '#E5E5E5',
              borderRadius: '10px',
              padding: '3px',
              gap: 0,
              minWidth: '280px',
              width: 'auto'
            }}
          >
            {[
              {
                key: 'groundFloor',
                label: 'Ground Floor',
                model: '/assets/ground_floor.glb',
                aria: 'View ground floor',
                variant: 'ground' as const
              },
              {
                key: 'firstFloor',
                label: 'First Floor',
                model: '/assets/first_floor.glb',
                aria: 'View first floor',
                variant: 'first' as const
              },
              {
                key: 'roof',
                label: 'Roof',
                model: '/assets/ceiling.glb',
                aria: 'View roof',
                variant: 'roof' as const
              }
            ].map((item) => {
              const isActive = activeModelButton === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => changeModel(item.model, item.key, item.variant)}
                  aria-pressed={isActive}
                  aria-label={item.aria}
                  style={{
                    display: 'block',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    margin: 0,
                    flex: 1,
                    minWidth: '0'
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      padding: '8px 12px',
                      borderRadius: isActive ? '8px' : '0',
                      backgroundColor: isActive ? '#FFFFFF' : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: isActive ? '600' : '400',
                        color: isActive ? '#000000' : '#666666',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif',
                        textAlign: 'center',
                        userSelect: 'none',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelViewer;